import { Cron } from '@modules/common/types';
import { CustomDayOfMonthSelector } from '@modules/subscribe/components/SubscribeDrawerV2/OutboundFrequencyCard/CustomDayOfMonthSelector';
import { ChevronRightRounded, InfoOutlined } from '@mui/icons-material';
import {
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { DayName } from 'generated/graphql';
import { useState } from 'react';

interface OutboundCardMonthlyProps {
  cronSchedule: Cron | undefined;
  setCronSchedule: (cronValue: Cron) => void;
}

export const OutboundCardMonthly = ({
  cronSchedule,
  setCronSchedule,
}: OutboundCardMonthlyProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [customDateMenuItemRef, setCustomDateMenuItemRef] =
    useState<HTMLLIElement | null>(null);
  const cronValue = cronSchedule?.valueV2;

  const getSelectedValue = () => {
    if (Boolean(customDateMenuItemRef)) {
      return 'custom-date'; // To show the "Custom Date" option as selected
    }

    if (Boolean(cronValue?.dayOfWeek?.length) && cronValue?.dayOfMonth?.last) {
      return 'last-monday';
    }

    if (Boolean(cronValue?.dayOfWeek?.length) && cronValue?.dayOfMonth?.day) {
      return 'first-monday';
    }

    if (cronValue?.dayOfMonth?.day === 1) {
      return 'first-day';
    }

    return 'custom-date';
  };

  const selectedValue = getSelectedValue();

  const handleChange = (event: SelectChangeEvent) => {
    switch (event.target.value) {
      case 'first-monday':
        setCronSchedule({
          valueV2: {
            hourOfDay: cronValue?.hourOfDay || 0,
            dayOfWeek: [DayName.Mon],
            dayOfMonth: {
              day: 1,
            },
          },
        });
        break;

      case 'last-monday':
        setCronSchedule({
          valueV2: {
            hourOfDay: cronValue?.hourOfDay || 0,
            dayOfWeek: [DayName.Mon],
            dayOfMonth: {
              last: true,
            },
          },
        });
        break;

      case 'first-day':
        setCronSchedule({
          valueV2: {
            hourOfDay: cronValue?.hourOfDay || 0,
            dayOfWeek: [],
            dayOfMonth: {
              day: 1,
            },
          },
        });
        break;

      case 'custom-date':
        event.stopPropagation();
        setCronSchedule({
          valueV2: {
            hourOfDay: cronValue?.hourOfDay || 0,
            dayOfWeek: [],
            dayOfMonth: {
              day: 1,
            },
          },
        });
        break;
    }
  };

  const openMonthDatePicker = (e: React.MouseEvent<HTMLLIElement>) => {
    e.stopPropagation();
    setCustomDateMenuItemRef(e.currentTarget);
  };

  const closeDayOfMonthSelector = () => {
    setCustomDateMenuItemRef(null);
    setIsMenuOpen(false);
  };

  const onCustomMonthDateChange = (value: number) => {
    setCronSchedule({
      valueV2: {
        hourOfDay: cronValue?.hourOfDay || 0,
        dayOfWeek: [],
        dayOfMonth: {
          day: value,
        },
      },
    });
    closeDayOfMonthSelector();
  };

  const customMonthDateValue = cronValue?.dayOfMonth?.day || 1;

  const getDayOfMonthLabel = (
    day: ReturnType<typeof getSelectedValue>,
    isInputView?: boolean,
  ) => {
    switch (day) {
      case 'first-monday':
        return 'First Monday of every month';
      case 'last-monday':
        return 'Last Monday of every month';
      case 'first-day':
        return '1st of every month';
      case 'custom-date':
        return customMonthDateValue === 1 && !isInputView
          ? 'Custom Date'
          : `${dayjs()
              .set('month', 0) // January for full month of 31 days
              .date(customMonthDateValue)
              .format('Do')} of every month`; // For formatting the date to 1st, 2nd, 3rd etc
      default:
        return '';
    }
  };

  const shouldShowFebruaryInfoTooltip =
    customMonthDateValue === 29 || customMonthDateValue === 30;

  return (
    <Stack direction="row" mt={3} alignItems="center" spacing={3}>
      <Typography>When</Typography>
      <FormControl>
        <InputLabel>Select Day</InputLabel>
        <Select
          open={isMenuOpen}
          onOpen={() => setIsMenuOpen(true)}
          onClose={() => setIsMenuOpen(false)}
          value={selectedValue}
          label="Select Day"
          onChange={handleChange}
          renderValue={(value) => getDayOfMonthLabel(value, true)}
        >
          <MenuItem value="first-day">
            {getDayOfMonthLabel('first-day')}
          </MenuItem>
          <MenuItem value="first-monday">
            {getDayOfMonthLabel('first-monday')}
          </MenuItem>
          <MenuItem value="last-monday">
            {getDayOfMonthLabel('last-monday')}
          </MenuItem>
          {/* Using 'onClickCapture' instead of 'onClick' to stop the event from closing menu */}
          <MenuItem value="custom-date" onClickCapture={openMonthDatePicker}>
            <ListItemText>{getDayOfMonthLabel('custom-date')}</ListItemText>
            <ChevronRightRounded color="primary" />
          </MenuItem>
        </Select>
      </FormControl>
      {shouldShowFebruaryInfoTooltip && (
        <Tooltip
          arrow
          title="When 29th or 30th is selected & the report month is February, your report will be sent alongside next month's."
          placement="top"
        >
          <InfoOutlined
            sx={{
              color: 'text.secondary',
            }}
          />
        </Tooltip>
      )}
      <CustomDayOfMonthSelector
        anchorEl={customDateMenuItemRef}
        onChange={onCustomMonthDateChange}
        value={customMonthDateValue}
        onClose={closeDayOfMonthSelector}
      />
    </Stack>
  );
};
